import { useCallback, useMemo } from "react";

import { cloneDeep } from "lodash-es";

import { useAuth } from "@/auth";
import { MegaMenuItemData } from "@/common/contracts/mega-menu.contracts";
import {
  GUEST_TAG,
  OVERRIDE_TAG,
  USER_TAG,
} from "@/common/static/mega-menu.static";
import MegaMenuPreval from "@/components/mega-menu/preval/MegaMenu.preval";
import { PRO_BUYER_PROGRAM_ENABLED } from "@/config";
import {
  OPEN_HOME_PUBLIC_GATE,
  useOpenHomeGate,
} from "@/features/open-home/gate";
import { withOpenHomeMenuItem } from "@/features/open-home/utils/open-home-nav.utils";
import { withProBuyerMenuItem } from "@/features/pro-buyer-application/utils/pro-buyer-nav.utils";
import { useContentLocale } from "@/i18n/hooks";

const ACCESS_ROLE_REGEX = new RegExp(`(${USER_TAG})|(${GUEST_TAG})`);
const OVERRIDE_HREF_REGEX = new RegExp(`${OVERRIDE_TAG}=(.*)$`);

type useMegaMenuDataOptions = {
  mobile: boolean;
};

export const useMegaMenuData = (options?: useMegaMenuDataOptions) => {
  const { isLoggedIn } = useAuth();
  const locale = useContentLocale();
  // The menu entry needs the public gate too: off while Open Home is shared by link only.
  const openHomeGate = useOpenHomeGate();
  const openHomePublic = useOpenHomeGate(OPEN_HOME_PUBLIC_GATE);
  const openHomeOn = openHomeGate && openHomePublic;

  const overrideMobileItemHref = useCallback((item: MegaMenuItemData) => {
    if (item.items) {
      item.items = item.items.map(overrideMobileItemHref);
    }

    if (item.href?.startsWith("/http")) {
      return { ...item, href: item.href?.substring(1) };
    }

    return { ...item, href: item.href?.replace(ACCESS_ROLE_REGEX, "") };
  }, []);

  const filterMobileItemByAccess = useCallback(
    (item: MegaMenuItemData) => {
      if (item.items) {
        item.items = item.items.filter(filterMobileItemByAccess);
      }

      if (item.href?.includes(OVERRIDE_TAG)) {
        return isLoggedIn
          ? item.href?.endsWith(USER_TAG)
          : item.href?.endsWith(GUEST_TAG);
      }

      return true;
    },
    [isLoggedIn]
  );

  /**
   * Gets mobile menu from preval, filters some items whether
   * the user is logged-in or not.
   */
  const loadMobileMenu = useCallback(
    (menu: { items: MegaMenuItemData[] }) => {
      return cloneDeep(menu.items)
        .filter(filterMobileItemByAccess)
        .map(overrideMobileItemHref);
    },
    [filterMobileItemByAccess, overrideMobileItemHref]
  );

  const overrideItemHref = useCallback((item: MegaMenuItemData) => {
    if (item.submenu?.items) {
      item.submenu.items = item.submenu.items.map(overrideItemHref);
    }

    if (item.items) {
      item.items = item.items.map(overrideItemHref);
    }

    if (item.href?.includes(OVERRIDE_TAG)) {
      const overrideHref = OVERRIDE_HREF_REGEX.exec(item.href);

      if (overrideHref && overrideHref.length === 2) {
        return { ...item, href: overrideHref[1] };
      }

      return item;
    }

    return item;
  }, []);

  /** Gets main menu and mobile menu from preval. */
  const load = useCallback(() => {
    try {
      const megaMenu = MegaMenuPreval.menu[locale];
      const mobileMenu = MegaMenuPreval.mobile[locale];

      const mobileItems = options?.mobile ? loadMobileMenu(mobileMenu) : [];
      const items = [
        ...(openHomeOn
          ? withOpenHomeMenuItem(megaMenu.items.map(overrideItemHref), locale)
          : megaMenu.items.map(overrideItemHref)),
        ...mobileItems,
      ];

      return {
        ...megaMenu,
        /* Desktop has the Pro Buyer button next to Verkoop instead (WHO-6571). */
        items: withProBuyerMenuItem(
          items,
          PRO_BUYER_PROGRAM_ENABLED && !!options?.mobile
        ),
      };
    } catch (ex) {
      console.error(ex);
    }
  }, [loadMobileMenu, locale, options?.mobile, overrideItemHref, openHomeOn]);

  const data = useMemo(() => {
    return load();
  }, [load]);

  return { data };
};
